import React, { useState, useEffect } from 'react';
import { CalendarDays, User, Clock, AlertCircle, CheckCircle, Star } from 'lucide-react';
import '../styles/email-content.css';
import { Card, Space, Typography, Tag, Button, Avatar } from 'antd';
import { UserOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { formatDateTime } from '../utils/timezone';
import { Email } from '../types';

const { Text, Paragraph } = Typography;

interface EmailConversationProps {
  emails: Email[];
  subject: string;
  onStatusChange: (emailId: number, status: string) => void;
  onReply: (threadId: number) => void;
  onBack?: () => void;
}

const EmailConversation: React.FC<EmailConversationProps> = ({ 
  emails, 
  subject, 
  onStatusChange, 
  onReply,
  onBack
}) => {
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set([emails[emails.length - 1]?.id]));

  const toggleEmailExpansion = (emailId: number) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const getEmailPreview = (content: string) => {
    // 移除HTML标签并截取前100个字符
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 100 ? textContent.substring(0, 100) + '...' : textContent;
  };

  const sortedEmails = [...emails].sort((a, b) => 
    new Date(a.received_at).getTime() - new Date(b.received_at).getTime()
  );

  return (
    <div className="h-full flex flex-col">
      {onBack && (
        <div style={{ 
          padding: '16px 24px', 
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div>
            <Button 
              type="text" 
              onClick={onBack}
              style={{ marginRight: 16 }}
            >
              ← 返回会话列表
            </Button>
            <Text strong style={{ fontSize: '16px' }}>{subject}</Text>
          </div>
          <Text type="secondary">{emails.length} 封邮件</Text>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto">
        <Card 
          style={{ marginBottom: 16, border: onBack ? 'none' : undefined }}
          bodyStyle={{ padding: 0 }}
        >
      {sortedEmails.map((email, index) => {
        const isExpanded = expandedEmails.has(email.id);
        const isLastEmail = index === sortedEmails.length - 1;
        const isSent = email.email_type === 'sent';
        
        return (
          <div key={email.id} style={{ borderBottom: index < sortedEmails.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            <div 
              style={{ 
                padding: '12px 24px',
                cursor: 'pointer',
                backgroundColor: isExpanded ? '#fafafa' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleEmailExpansion(email.id)}
            >
              <div style={{ flex: 1 }}>
                <Space>
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />} 
                    style={{ 
                      backgroundColor: isSent ? '#52c41a' : '#1890ff',
                      fontSize: '12px'
                    }}
                  >
                    {isSent ? '我' : (email.sender_name?.[0] || email.sender_email[0]).toUpperCase()}
                  </Avatar>
                  <div>
                    <Text strong>
                      {isSent ? '我' : (email.sender_name || email.sender_email)}
                    </Text>
                    {!isSent && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        &lt;{email.sender_email}&gt;
                      </Text>
                    )}
                    <div>
                      <Space size="small">
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatDateTime(email.received_at, { format: 'short' })}
                        </Text>
                        {email.status === 'unread' && !isSent && (
                          <Tag color="blue">未读</Tag>
                        )}
                      </Space>
                    </div>
                  </div>
                </Space>
                
                {!isExpanded && (
                  <div style={{ marginTop: 4, marginLeft: 32 }}>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      {getEmailPreview(email.content)}
                    </Text>
                  </div>
                )}
              </div>
              
              <Button 
                type="text" 
                size="small" 
                icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              />
            </div>
            
            {isExpanded && (
              <div style={{ padding: '0 24px 24px 56px' }}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    收件人: {email.recipient_email}
                  </Text>
                </div>
                
                <div 
                  style={{ 
                    backgroundColor: '#fafafa',
                    padding: '16px',
                    borderRadius: '6px',
                    marginBottom: 16
                  }}
                >
                  {email.html_content ? (
                    <div dangerouslySetInnerHTML={{ __html: email.html_content }} />
                  ) : (
                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {email.content}
                    </Paragraph>
                  )}
                </div>
                
                {isLastEmail && !isSent && (
                  <Space>
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReply(email.id);
                      }}
                    >
                      回复
                    </Button>
                    {email.status === 'unread' && (
                      <Button 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusChange(email.id, 'read');
                        }}
                      >
                        标记已读
                      </Button>
                    )}
                  </Space>
                )}
              </div>
            )}
          </div>
        );
      })}
        </Card>
      </div>
    </div>
  );
};

export default EmailConversation; 